/**
 * Konum ne zaman okunur, ne zaman gönderilir (9.3.3.3 · D-580).
 *
 * Saf katman: GPS, ağ ve React burada yok. Sebebi enerji kuralının kararın
 * kendisi olması — ve bir telefona ihtiyaç duymadan sınanabilmesi gerekiyor.
 *
 * Google Maps ve Find My konumu sabit bir sıklıkla iter. Bizim iddiamız daha
 * düşük enerji, ve o iddia iki şeyden geliyor: **duran telefon neredeyse hiç
 * ölçmez** (aşağıdaki hız uyarlamalı gecikme) ve **bakan yoksa hiç ölçmez**
 * (9.3.3.4). Bu dosya birincisi.
 */

/** Dünyanın yarıçapı; iki nokta arası mesafe için. */
const DUNYA_YARICAPI_M = 6_371_000;

export type Point = {
  latitude: number;
  longitude: number;
  /** Ölçümün kendi hata payı (metre). Bilinmiyorsa null. */
  accuracy?: number | null;
  /** Metre/saniye. Bilinmiyorsa null; o zaman noktalardan türetilir. */
  speed?: number | null;
};

export function distanceMeters(a: Point, b: Point): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * DUNYA_YARICAPI_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Bir sonraki ölçüme kadar beklenecek süre, hıza göre.
 *
 * Duran telefonda iki dakika: masada duran bir telefonu saniyede bir yoklamak,
 * hiçbir şey öğrenmeden pil harcamaktır. Araçtaki telefonda on saniye: orada
 * iki dakika, karşı tarafa kilometrelerce eski bir nokta göstermek olurdu.
 *
 * Aradaki değerler basamaklı, sürekli değil: sürekli bir eğri, hız ölçümünün
 * gürültüsünü doğrudan ölçüm sıklığına çevirirdi.
 */
export const MIN_READ_DELAY_SECONDS = 10;
export const MAX_READ_DELAY_SECONDS = 120;

export function nextReadDelaySeconds(speedMps: number | null | undefined): number {
  const hiz = typeof speedMps === 'number' && Number.isFinite(speedMps) && speedMps > 0 ? speedMps : 0;
  if (hiz < 0.5) return MAX_READ_DELAY_SECONDS;   // duruyor
  if (hiz < 2) return 60;                          // yürüyor
  if (hiz < 8) return 25;                          // koşuyor / bisiklet
  return MIN_READ_DELAY_SECONDS;                   // araçta
}

/**
 * Ölçülen her nokta GÖNDERİLMEZ.
 *
 * Gönderme ölçmekten pahalıdır (radyo uyanır) ve duran bir telefonun GPS
 * gürültüsü metrelerce zıplar. Anlamlı değişim yoksa gönderilmiyor; ama
 * bir de düzenli **nabız** var, yoksa karşı taraf bağlantının mı koptuğunu
 * yoksa kişinin mi durduğunu ayırt edemezdi.
 */
export const MIN_SEND_DISTANCE_M = 25;
export const SEND_HEARTBEAT_SECONDS = 180;
/** Hata payı bundan kötü olan ölçüm bir şey söylemiyor. */
export const MAX_ACCURACY_M = 100;

export type SendDecision = 'send' | 'skip_inaccurate' | 'skip_unmoved';

export function decideSend(input: {
  current: Point;
  lastSent: Point | null;
  secondsSinceLastSend: number;
}): SendDecision {
  const { current, lastSent, secondsSinceLastSend } = input;
  const hata = current.accuracy;
  if (typeof hata === 'number' && Number.isFinite(hata) && hata > MAX_ACCURACY_M) {
    return 'skip_inaccurate';
  }
  // İlk nokta her zaman gider: karşı tarafın gösterecek bir şeyi olmalı.
  if (!lastSent) return 'send';
  if (secondsSinceLastSend >= SEND_HEARTBEAT_SECONDS) return 'send';
  return distanceMeters(lastSent, current) >= MIN_SEND_DISTANCE_M ? 'send' : 'skip_unmoved';
}
